import { useState, useEffect, useRef, useContext } from "react";
import PropTypes from "prop-types";
import classnames from "classnames/bind";
import { ERROR_ORIGIN } from "../../ErrorAlert/constants";
import { ADD_SAVED_PRODUCT } from "../../../state/modules/savedItems/constants";
import { saveProductForLater } from "../../../client/saveForLater";
import Icon from "../../Icon";
import styles from "./SaveForLater.css";
import { getAriaLabelKey } from "./getAriaLabelKey";
import logger from "../../../lib/logger";
import { TILE_SIZES } from "../../../helpers/constants";
import TileContext from "../../TileContext";

const { TILE_SIZE_TWO_BY_ONE, TILE_SIZE_TWO_BY_TWO } = TILE_SIZES;

const cx = classnames.bind(styles);
const VALENTINES = "valentines";

const SaveForLater = ({
  productId,
  price,
  reducedPrice,
  colourWayId,
  analyticsSaveForLater,
  saveProductInState,
  removeSavedProduct,
  setError,
  isSaved,
  formatTranslation,
  eventIconType,
  carouselCatId,
  brandName,
  colour,
  productCode,
  description,
  onWishlistBeacon
}) => {
  const [animationFinishedCount, setAnimationFinishedCount] = useState(0);
  const [showFlutterAnimationIcons, setShowFlutterAnimationIcons] = useState(
    false
  );
  const flutter = useRef([]);
  const { tileSize } = useContext(TileContext);

  const handleSave = () => {
    saveProductForLater({
      productId,
      colourWayId
    }).catch(error => {
      removeSavedProduct(productId);

      logger.error(error);

      if (!isSaved) {
        setError({
          message: error.message,
          actionType: ADD_SAVED_PRODUCT,
          errorOrigin: ERROR_ORIGIN.saveItem
        });
      }
    });

    saveProductInState(productId);
    analyticsSaveForLater({
      productId,
      reducedPrice,
      price,
      carouselCatId,
      brandName,
      colour,
      productCode,
      description
    });

    if (onWishlistBeacon) {
      navigator.sendBeacon(onWishlistBeacon);
    }

    setAnimationFinishedCount(0);
    setShowFlutterAnimationIcons(true);
    move();
  };

  const handleAnimationEnd = () => {
    if (animationFinishedCount === flutter.current.length - 1) {
      setShowFlutterAnimationIcons(false);
      setAnimationFinishedCount(0);
    } else {
      setAnimationFinishedCount(animationFinishedCount + 1);
    }
  };

  const move = () => {
    const celebrationStyle = classnames(styles.flutter, {
      [styles[eventIconType]]: eventIconType
    });

    flutter.current.forEach(heart => {
      heart.className = celebrationStyle;
    });

    if (eventIconType === VALENTINES) {
      return;
    }

    const directionX = Math.random() > 0.5 ? -1 : 1;
    const directionY = Math.random() > 0.5 ? -1 : 1;
    const distance = eventIconType ? 60 : 40;

    flutter.current.forEach(heart => {
      heart.style.setProperty(
        "--translateX",
        Math.random() * distance * directionX
      );
      heart.style.setProperty(
        "--translateY",
        Math.random() * distance * directionY
      );
    });
  };

  const flutterCount = eventIconType === VALENTINES ? 5 : 2;

  useEffect(() => {
    if (isSaved && showFlutterAnimationIcons) {
      move();
    } else {
      flutter.current = [];
    }
  }, [isSaved, showFlutterAnimationIcons, flutterCount]);

  return (
    <button
      type="button"
      disabled={isSaved}
      className={cx(styles.saveForLater, {
        [styles.saved]: isSaved,
        [styles.animated]: showFlutterAnimationIcons,
        [styles.twoByOne]: tileSize === TILE_SIZE_TWO_BY_ONE,
        [styles.twoByTwo]: tileSize === TILE_SIZE_TWO_BY_TWO
      })}
      onClick={handleSave}
      aria-label={formatTranslation(getAriaLabelKey({ isSaved }))}
      aria-pressed={isSaved}
    >
      <Icon icon="heart" />
      {isSaved &&
        showFlutterAnimationIcons &&
        [...new Array(flutterCount)].map((_, flutterIndex) => (
          <span
            key={flutterIndex}
            onAnimationEnd={handleAnimationEnd}
            ref={node => (flutter.current[flutterIndex] = node)}
          />
        ))}
    </button>
  );
};

SaveForLater.propTypes = {
  isSaved: PropTypes.bool.isRequired,
  productId: PropTypes.number.isRequired,
  price: PropTypes.number.isRequired,
  reducedPrice: PropTypes.number,
  colourWayId: PropTypes.number.isRequired,
  formatTranslation: PropTypes.func,
  saveProductInState: PropTypes.func.isRequired,
  removeSavedProduct: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  analyticsSaveForLater: PropTypes.func.isRequired,
  eventIconType: PropTypes.string,
  carouselCatId: PropTypes.number,
  brandName: PropTypes.string,
  colour: PropTypes.string,
  productCode: PropTypes.number,
  description: PropTypes.string,
  onWishlistBeacon: PropTypes.string
};

export default SaveForLater;
